import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, FileSpreadsheet, FileText, Paperclip, ChevronDown, ExternalLink, CircleHelp, ShieldCheck, Eye, Clock, Reply } from "lucide-react";
import { cn } from "@/lib/utils";
import { Monogram, LightMarkdown } from "@/components/common";
import { fileSize, fmtDateTime } from "@/lib/format";
import { compactMoney } from "@/lib/sk";

const Card = ({ title, children, className, testId, right }) => (
  <section className={cn("border-t border-black/[0.06] pt-5", className)} data-testid={testId}>
    {title ? <header className="flex items-center justify-between gap-3"><h3 className="text-[12.5px] font-semibold text-muted-foreground">{title}</h3>{right}</header> : null}
    <div className={cn(title ? "pt-3" : "")}>{children}</div>
  </section>
);

export function ContactCard({ p }) {
  const c = p.contact || {};
  const em = p.email;
  if (!c.name && !c.email && !em) return null;
  return (
    <Card testId="inbox-reader-contact">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Monogram name={c.name || c.email || "?"} size="md" className="h-10 w-10 rounded-full" />
          <div className="leading-tight">
            <div className="text-[14px] font-semibold" data-testid="inbox-reader-contact-name">{c.name || "Ukjent kontakt"}</div>
            <div className="text-[12.5px] text-muted-foreground">{[c.role, p.customer?.name].filter(Boolean).join(" · ")}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.email ? <a href={`mailto:${c.email}`} className="inline-flex h-8 items-center gap-1.5 rounded-full border border-black/[0.08] px-3 text-[12.5px] font-medium hover:border-black/[0.2]" data-testid="inbox-reader-contact-email"><Mail className="h-3.5 w-3.5 text-primary" />{c.email}</a> : null}
          {c.phone ? <a href={`tel:${c.phone}`} className="inline-flex h-8 items-center gap-1.5 rounded-full border border-black/[0.08] px-3 text-[12.5px] font-medium hover:border-black/[0.2]" data-testid="inbox-reader-contact-phone"><Phone className="h-3.5 w-3.5 text-primary" />{c.phone}</a> : null}
        </div>
      </div>
      {em ? (
        <dl className="mt-4 grid grid-cols-[64px_1fr] gap-x-3 gap-y-1 border-t border-black/[0.05] pt-3 text-[12.5px]" data-testid="inbox-reader-email-meta">
          <dt className="text-muted-foreground">Fra</dt><dd className="truncate">{em.from}</dd>
          <dt className="text-muted-foreground">Til</dt><dd className="truncate">{em.to}</dd>
          {em.cc?.length ? <><dt className="text-muted-foreground">Kopi</dt><dd className="truncate">{em.cc.join(", ")}</dd></> : null}
          <dt className="text-muted-foreground">Mottatt</dt><dd>{fmtDateTime(em.received_at)}</dd>
        </dl>
      ) : null}
    </Card>
  );
}

export function MessageCard({ p }) {
  const first = (p.messages || []).find((m) => m.role === "user" && m.content && !/^Request via guided intake/i.test(m.content));
  const text = (first?.content || (p.scope || {}).notes || p.summary || "").replace(/^Request via guided intake \([a-z]+\)\.\s*/i, "").trim();
  if (!text) return null;
  return (
    <Card title={p.email ? "E-posten" : p.intake ? "Kundens beskrivelse" : "Henvendelsen"} testId="inbox-reader-message">
      <div className="whitespace-pre-wrap text-[15px] leading-[1.7] text-foreground/90" data-testid="inbox-reader-body">{text}</div>
    </Card>
  );
}

export function Attachments({ p, st }) {
  const files = p.files || [];
  if (!files.length) return null;
  return (
    <Card title={`Vedlegg · ${files.length}`} testId="inbox-reader-files">
      <ul className="grid gap-2 sm:grid-cols-2">
        {files.map((f) => {
          const isSheet = f.kind === "valve_list" || /\.(xlsx|xlsm|xls|csv)$/i.test(f.name || "");
          const Icon = isSheet ? FileSpreadsheet : /\.pdf$/i.test(f.name || "") ? FileText : Paperclip;
          return (
            <li key={f.id || f.name} className="flex items-center gap-3 rounded-xl border border-black/[0.06] bg-[hsl(210_20%_98.5%)] px-3 py-2.5" data-testid={`inbox-reader-file-${f.id || f.name}`}>
              <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", isSheet ? "bg-[hsl(var(--brand-soft))] text-primary" : "bg-black/[0.05] text-muted-foreground")}><Icon className="h-4 w-4" /></span>
              <span className="min-w-0 flex-1 leading-tight">
                <span className="block truncate text-[13px] font-medium">{f.name}</span>
                <span className="block text-[11.5px] text-muted-foreground">{[f.size ? fileSize(f.size) : null, isSheet && st?.total ? `${st.total} ventiler lest` : f.kind === "document" ? "Dokument – ikke ventilliste" : null].filter(Boolean).join(" · ")}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export function AiRead({ p, st }) {
  const sc = p.scope || {};
  const v = p.vessel || {};
  const facts = [["Fartøy", [v.name, v.hull_no && v.hull_no !== v.name ? v.hull_no : null].filter(Boolean).join(" · ")], ["Type", v.type], ["Klasse", v.class_society], ["Verft", v.yard], ["Leveranse", sc.delivery_deadline], ["Ventiler (oppgitt)", sc.valve_count_hint ? `ca. ${sc.valve_count_hint}` : null]].filter(([, val]) => val);
  const max = Math.max(1, ...(st?.by_system || []).map((s) => s.count));
  return (
    <Card title="KI har lest" testId="inbox-reader-ai" right={<span className="text-[11px] text-muted-foreground">Deterministisk fra ventilliste og tekst</span>}>
      <div className="grid gap-5 md:grid-cols-2">
        <div>
          {st?.total ? (
            <>
              <div className="flex items-baseline gap-2"><span className="font-display text-[30px] font-bold tracking-[-0.03em] text-[hsl(var(--navy))]" data-testid="inbox-reader-valves-total">{st.total}</span><span className="text-[13px] text-muted-foreground">ventiler lest</span></div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px]">
                <span className="text-[hsl(var(--v-ok))]"><b className="font-mono">{st.mapped}</b> komplette</span>
                {st.needs_clarification ? <span className="text-[hsl(var(--v-assumed))]"><b className="font-mono">{st.needs_clarification}</b> må avklares</span> : null}
                {st.needs_engineer ? <span className="text-[hsl(var(--v-eng))]"><b className="font-mono">{st.needs_engineer}</b> ingeniør</span> : null}
              </div>
              <ul className="mt-3 space-y-1.5">
                {(st.by_system || []).map((s) => (
                  <li key={s.name} className="flex items-center gap-2 text-[12px]"><span className="w-[88px] shrink-0 truncate text-muted-foreground">{s.name}</span><span className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.05]"><span className="block h-full rounded-full bg-[hsl(var(--navy))]" style={{ width: `${(s.count / max) * 100}%` }} /></span><span className="w-6 text-right font-mono tabular-nums">{s.count}</span></li>
                ))}
              </ul>
            </>
          ) : (
            <div className="rounded-xl bg-[hsl(var(--v-assumed-bg))] px-3.5 py-3 text-[12.5px] text-[hsl(var(--v-assumed))]" data-testid="inbox-reader-no-valves"><b>Ventilliste mangler.</b> {sc.valve_count_hint ? `Kunden anslår ca. ${sc.valve_count_hint} ventiler.` : "Ingen antall oppgitt."} Når listen kommer, leses den automatisk.</div>
          )}
          {sc.systems?.length ? <div className="mt-3 flex flex-wrap gap-1.5">{sc.systems.map((s) => <span key={s} className="rounded-full bg-black/[0.05] px-2 py-0.5 text-[11.5px] font-medium">{s}</span>)}</div> : null}
        </div>
        <div>
          {facts.length ? <dl className="divide-y divide-black/[0.05] text-[13px]">{facts.map(([k, val]) => <div key={k} className="flex justify-between gap-3 py-1.5"><dt className="text-muted-foreground">{k}</dt><dd className="text-right font-medium">{val}</dd></div>)}</dl> : null}
          {p.open_questions?.length ? (
            <div className="mt-3">
              <div className="flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground"><CircleHelp className="h-3.5 w-3.5" /> Åpne spørsmål</div>
              <ul className="mt-1.5 space-y-1 text-[13px]">{p.open_questions.map((qq) => <li key={qq} className="flex gap-2"><span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-primary" />{qq}</li>)}</ul>
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

export function Transcript({ p }) {
  const [open, setOpen] = useState(false);
  const msgs = (p.messages || []).filter((m) => m.content);
  if (!p.intake || msgs.length < 2) return null;
  return (
    <Card testId="inbox-reader-transcript">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between text-left text-[13px] font-semibold" data-testid="inbox-reader-transcript-toggle">Samtalen med assistenten · {msgs.length} meldinger <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", open && "rotate-180")} /></button>
      {open ? (
        <ol className="mt-4 space-y-3">
          {msgs.map((m) => <li key={m.id} className={cn("max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed", m.role === "user" ? "ml-auto bg-[hsl(var(--navy))] text-white" : "bg-black/[0.04]")}><LightMarkdown text={m.content} /></li>)}
        </ol>
      ) : null}
    </Card>
  );
}

export function ApprovalCard({ item, ctx, canApprove, onApprove, busy }) {
  const cfg = ctx?.config || {};
  const q = ctx?.quote || {};
  const eng = item.meta?.kind === "engineering";
  const t = (eng ? cfg.totals : q.totals) || {};
  const val = cfg.validation || {};
  return (
    <Card title={eng ? "Konfigurasjonen" : "Tilbudet"} testId="inbox-reader-approval">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[16px] font-semibold">{eng ? `«${cfg.name || "Basis"}»` : `Tilbud v${q.version || 1}`}</div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
            <span>Sum <b className="font-mono text-foreground">{compactMoney(t.total_sales)}</b></span>
            {t.gm_pct != null ? <span>DB <b className="font-mono text-foreground">{t.gm_pct}%</b></span> : null}
            {eng ? <span>Validering <b className="text-foreground">{val.status || "–"}</b></span> : <span>{(q.approval || {}).label}</span>}
          </div>
        </div>
        {canApprove ? <button type="button" disabled={busy} onClick={onApprove} className="inline-flex h-10 items-center gap-2 rounded-full bg-[hsl(var(--v-ok))] px-4 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50" data-testid="inbox-reader-approve"><ShieldCheck className="h-4 w-4" /> {eng ? "Gi ingeniørgodkjenning" : "Godkjenn prisen"}</button> : <span className="text-[12.5px] text-muted-foreground">Du har ikke godkjenningsrett for dette.</span>}
      </div>
      {eng && val.issues?.length ? <ul className="mt-4 space-y-1 border-t border-black/[0.05] pt-3 text-[13px]">{val.issues.slice(0, 6).map((i, k) => <li key={k} className="flex gap-2"><span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-[hsl(var(--v-assumed))]" />{i.message || i}</li>)}</ul> : null}
    </Card>
  );
}

export function QuoteCard({ item, ctx }) {
  const q = ctx?.quote || {};
  const m = item.meta || {};
  const Icon = item.type === "quote" ? Eye : Clock;
  return (
    <Card title={item.type === "quote" ? "Kundens aktivitet" : "Status"} testId="inbox-reader-quote">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-primary"><Icon className="h-5 w-5" /></span>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold">{item.title}</div>
            <div className="mt-0.5 text-[12.5px] text-muted-foreground">{item.type === "quote" ? `Sist åpnet ${fmtDateTime(q.last_viewed_at || item.at)} · sendt ${fmtDateTime(q.sent_at)}` : `Sendt ${fmtDateTime(q.sent_at)} · gyldig til ${q.valid_until || "–"}`}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {q.token ? <a href={`/q/${q.token}`} target="_blank" rel="noreferrer" className="inline-flex h-9 items-center gap-1.5 rounded-full border border-black/[0.08] px-3.5 text-[12.5px] font-medium hover:border-black/[0.2]" data-testid="inbox-reader-quote-public">Slik kunden ser det <ExternalLink className="h-3.5 w-3.5" /></a> : null}
          <Link to={`/p/${item.project.id}?tab=tilbud`} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[hsl(var(--navy))] px-3.5 text-[12.5px] font-semibold text-white hover:bg-[hsl(var(--ink-2))]" data-testid="inbox-reader-quote-open">Åpne tilbudet</Link>
        </div>
      </div>
      {q.totals ? <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 border-t border-black/[0.05] pt-3 text-[13px] text-muted-foreground"><span>Sum <b className="font-mono text-foreground">{compactMoney(q.totals.total_sales, q.currency)}</b></span>{q.totals.gm_pct != null ? <span>DB <b className="font-mono text-foreground">{q.totals.gm_pct}%</b></span> : null}{m.views ? <span>Åpnet <b className="font-mono text-foreground">{m.views}</b> ganger</span> : null}</div> : null}
    </Card>
  );
}

export function ReplyCard({ item, p }) {
  const cl = p.clarification || {};
  return (
    <Card title="Kundesvaret" testId="inbox-reader-reply">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-primary"><Reply className="h-5 w-5" /></span>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold">{cl.count || ""} avklaringer besvart</div>
            <div className="mt-0.5 text-[12.5px] text-muted-foreground">{fmtDateTime(cl.answered_at)} · konfigurasjonen er regnet på nytt</div>
          </div>
        </div>
        <Link to={`/p/${p.id}?tab=krav`} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[hsl(var(--navy))] px-3.5 text-[12.5px] font-semibold text-white hover:bg-[hsl(var(--ink-2))]" data-testid="inbox-reader-reply-open">Se kravene</Link>
      </div>
    </Card>
  );
}
